import React, {useState} from 'react';
import styled from 'styled-components';
import {useHistory, useParams} from 'react-router-dom';
import Table from 'components/Tables/TableStructure/Table';
import Paper from 'components/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import {DeviceWithLocation} from 'features/SiteSearch/interfaces/DeviceResponse';
import MultipleSelectionWarningModal from 'features/SiteSearch/components/MultipleSelectionWarningModal';
import getSwitchURL from 'features/SiteSearch/functions/getSwitchURL';

const StyledTable = styled(Table)``;

const StyledCheckbox = styled(Checkbox)`
  color: ${props => props.theme.text};
  padding: 2px;
  &.Mui-checked {
    color: ${props => props.theme.text};
  }
`;

const StyledLink = styled.a`
  color: ${props => props.theme.text};
  cursor: pointer;
  transition: color 200ms;
  &:hover {
    text-decoration: underline;
    color: ${props => props.theme.activeStateBlue};
  }
`;

interface SiteSearchResultsTableProps {
  data: DeviceWithLocation[];
  loaded: boolean;
  handleDeviceSelection: (deviceName: string[]) => void;
}

export default function SiteSearchResultsTable({
  data,
  loaded,
  handleDeviceSelection,
}: SiteSearchResultsTableProps) {
  const {urlKey} = useParams<any>();
  let history = useHistory();

  const [state, setState] = useState<string[]>([]);
  const [tempSelection, setTempSelection] = useState<string>();
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const index = state.indexOf(event.target.name);

    if (index > -1) {
      const newState = [...state];
      newState.splice(index, 1);
      setState(newState);
      handleDeviceSelection(newState);
    } else if (state.length + 1 === 10) {
      setIsWarningModalOpen(true);
      setTempSelection(event.target.name);
    } else {
      setState([...state, event.target.name]);
      handleDeviceSelection([...state, event.target.name]);
    }
  };

  const handleModalClose = () => setIsWarningModalOpen(false);

  const handleConfirmSelection = () => {
    if (!tempSelection) return;

    setState([...state, tempSelection]);
    handleDeviceSelection([...state, tempSelection]);
    setTempSelection(undefined);

    setIsWarningModalOpen(false);
  };

  const handleNavToSwitches = (switchStackName: string) => {
    const switchStackInfo = data.filter(switchStack => switchStack.deviceName === switchStackName);
    if (switchStackInfo.length === 0) return;
    const switchURL = getSwitchURL({urlKey, selectedDevices: switchStackInfo});
    history.push(switchURL);
  };

  const columns = [
    {
      id: 'deviceName',
      width: '50px',
      format: (value: string, row: any) => (
        <StyledCheckbox
          checked={state.includes(value)}
          onChange={handleChange}
          name={value}
          color="primary"
          inputProps={{'aria-label': value}}
        />
      ),
    },
    {
      id: 'deviceName',
      width: '160px',
      label: 'Switch Stack Name',
      format: (value: string) => (
        <StyledLink onClick={() => handleNavToSwitches(value)}>{value}</StyledLink>
      ),
    },
    {
      id: 'location',
      width: '300px',
      label: 'Location',
    },
    {
      id: 'manufacturer',
      label: 'Manufacturer',
    },
    {
      id: 'model',
      label: 'Model',
    },
    {
      id: 'serialNumber',
      label: 'Serial Number',
    },
  ];

  return (
    <Paper>
      <StyledTable
        columns={columns}
        data={data}
        paginationText="Switch Stacks"
        loaded={loaded}
        customRowsPerPage={{defaultRowsPerPage: 10}}
      />

      <MultipleSelectionWarningModal
        isModalOpen={isWarningModalOpen}
        handleClose={handleModalClose}
        handleConfirm={handleConfirmSelection}
      />
    </Paper>
  );
}
